import { CAMPAIGN_CATEGORIES } from "../data/categories";

/**
 * Horizontal chip tape for browsing campaigns by category.
 *
 * - Renders an "All" chip first (selected = null), then one chip per
 *   category from data/categories.js.
 * - Chips are ink-outline pills that flip to ink-filled when active,
 *   matching the site's ink-filled twin CTA system.
 * - Horizontally scrollable on mobile (13 categories won't fit); wraps
 *   naturally on wide viewports via flex-wrap.
 * - Emits `onSelect(slug | null)` on click.
 *
 * Props:
 *   selected: string | null — currently active category slug (null = All)
 *   onSelect: (slug | null) => void
 *   className: pass-through wrapper class
 */
const Chip = ({ active, disabled, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    aria-disabled={disabled || undefined}
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-base font-medium transition-colors ${ active ? "border-ink bg-ink text-sand" : disabled ? "border-border/60 bg-transparent text-olive/50" : "border-border bg-transparent text-ink hover:border-ink hover:bg-bone" }`}
  >
    {children}
  </button>
);

// Small pill showing the count next to a chip label. Sand-tinted when the
// chip is inked (active), olive/8 otherwise.
const Count = ({ n, active }) => (
  <span
    className={`ml-0.5 rounded-full px-1.5 text-[13px] font-semibold tabular-nums ${ active ? "bg-sand/25 text-sand" : "bg-olive/12 text-olive" }`}
  >
    {n}
  </span>
);

export const CategoryChips = ({ selected, onSelect, counts = {}, className = "" }) => {
  // "All" chip shows the sum of all category counts (i.e., total campaigns
  // matching whatever OTHER filters are on).
  const totalCount = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
  return (
  <div
    className={`flex flex-wrap gap-2 overflow-x-auto no-scrollbar pb-1 ${className}`}
    data-testid="category-chips"
    role="group"
    aria-label="Filter campaigns by category"
  >
    <Chip
      active={selected == null}
      onClick={() => onSelect(null)}
      testid="category-chip-all"
    >
      All
      <Count n={totalCount} active={selected == null} />
    </Chip>

    {CAMPAIGN_CATEGORIES.map((cat) => {
      const Icon = cat.Icon;
      const n = counts[cat.slug] || 0;
      const active = selected === cat.slug;
      return (
        <Chip
          key={cat.slug}
          active={active}
          disabled={!active && n === 0}
          onClick={() => onSelect(cat.slug)}
          testid={`category-chip-${cat.slug}`}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {cat.label}
          <Count n={n} active={active} />
        </Chip>
      );
    })}
  </div>
  );
};
